import React, { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom RangeSlider Component
 * A dual-thumb slider for selecting a range (min/max) within a bounded value set.
 *
 * This implementation uses native HTML range inputs styled with Tailwind CSS,
 * overlaid to create a single track with a filled range indicator.
 *
 * @param {number} min - The minimum allowed value for the range.
 * @param {number} max - The maximum allowed value for the range.
 * @param {number} step - The increment size.
 * @param {[number, number]} value - The current selected range [start, end].
 * @param {function} onChange - Callback function triggered when the value changes.
 */
const RangeSlider = ({ min, max, step = 1, value, onChange }) => {
	// Local state to manage the two thumb positions
	const [localMin, setLocalMin] = useState(value[0]);
	const [localMax, setLocalMax] = useState(value[1]);

	// Refs for the track and the range input elements
	const rangeRef = useRef(null);

	// Sync external 'value' prop changes to internal state
	useEffect(() => {
		setLocalMin(value[0]);
		setLocalMax(value[1]);
	}, [value]);

	// Helper function to calculate the percentage position of a value
	const getPercent = useCallback(
		(val) => {
			return ((val - min) / (max - min)) * 100;
		},
		[min, max]
	);

	// Calculate the left position and width for the filled track
	const minPercent = getPercent(localMin);
	const maxPercent = getPercent(localMax);

	// Handlers for thumb movement
	const handleMinChange = (e) => {
		// Ensure the new min is less than or equal to the current max
		const newMin = Math.min(Number(e.target.value), localMax);
		setLocalMin(newMin);
		onChange([newMin, localMax]);
	};

	const handleMaxChange = (e) => {
		// Ensure the new max is greater than or equal to the current min
		const newMax = Math.max(Number(e.target.value), localMin);
		setLocalMax(newMax);
		onChange([localMin, newMax]);
	};

	/**
	 * Tailwind CSS Customization for Range Input Thumbs
	 * We apply custom styles to hide the default browser thumb and implement
	 * a custom thumb appearance using pseudo-elements, making the component look
	 * uniform across different browsers and devices.
	 */
	const sliderStyles = `
    /* Common styling for both range inputs */
    .range-input {
      pointer-events: none; /* Allows clicks to pass through to the lower input */
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      width: 100%;
      height: 10px;
      position: absolute;
      top: 0;
      left: 0;
    }

    /* Track styling */
    .range-input::-webkit-slider-runnable-track {
      background: transparent;
      height: 6px;
      cursor: pointer;
    }
    .range-input::-moz-range-track {
      background: transparent;
      height: 6px;
      cursor: pointer;
    }

    /* Thumb styling (Webkit - Chrome, Safari) */
    .range-input::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      pointer-events: all;
      height: 20px;
      width: 20px;
      border-radius: 9999px; /* Tailwind 'rounded-full' */
      background: #4F46E5; /* Tailwind 'indigo-600' */
      border: 3px solid #FFFFFF; /* White border */
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); /* Shadow */
      margin-top: -7px; /* Adjust vertically to center on the track */
      transition: background 0.2s, transform 0.2s;
      cursor: grab;
    }
    .range-input:active::-webkit-slider-thumb {
        transform: scale(1.1);
        cursor: grabbing;
    }


    /* Thumb styling (Mozilla - Firefox) */
    .range-input::-moz-range-thumb {
      pointer-events: all;
      height: 20px;
      width: 20px;
      border-radius: 9999px;
      background: #4F46E5;
      border: 3px solid #FFFFFF;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
      transition: background 0.2s, transform 0.2s;
      cursor: grab;
    }
    .range-input:active::-moz-range-thumb {
        transform: scale(1.1);
        cursor: grabbing;
    }
  `;

	return (
		<div className="p-4 bg-white rounded-xl shadow-lg border border-gray-100 max-w-lg mx-auto">
			<style>{sliderStyles}</style>

			{/* 1. Value Display */}
			<div className="flex justify-between text-sm font-medium mb-4">
				<span className="text-gray-700">
					<span className="text-indigo-600 font-bold">{localMin.toLocaleString()}</span>
				</span>
				<span className="text-gray-700">
					<span className="text-indigo-600 font-bold">{localMax.toLocaleString()}</span>
				</span>
			</div>

			{/* 2. Slider Track Container */}
			<div className="relative h-6" ref={rangeRef}>
				{/* Unfilled Track (Base line) */}
				<div className="absolute top-1/2 left-0 right-0 h-2 bg-gray-200 rounded-full transform -translate-y-1/2"></div>

				{/* Filled Range Indicator */}
				<div
					className="absolute h-2 bg-indigo-500 rounded-full transform -translate-y-1/2"
					style={{
						left: `${minPercent}%`,
						width: `${maxPercent - minPercent}%`,
						top: "50%",
					}}
				></div>

				{/* --- Range Inputs (The Thumbs) --- */}

				{/* Input 1 (Min Thumb) */}
				<input
					type="range"
					min={min}
					max={max}
					step={step}
					value={localMin}
					onChange={handleMinChange}
					className="range-input z-20"
				/>

				{/* Input 2 (Max Thumb) */}
				<input
					type="range"
					min={min}
					max={max}
					step={step}
					value={localMax}
					onChange={handleMaxChange}
					className="range-input z-30"
				/>
			</div>

			{/* 3. Helper Text/Full Range */}
			<div className="flex justify-between text-xs text-gray-400 mt-2">
				<span>{min.toLocaleString()}</span>
				<span>{max.toLocaleString()}</span>
			</div>
		</div>
	);
};

// --- Example Usage Component ---

export default RangeSlider;
